import { Link } from "react-router-dom"
import type { EngagementResult } from "@/hooks/useInteractions"
import { parseRepostEvent } from "@/nostr/api/social"
import { KINDS, tagValue, type NostrEvent } from "@/nostr/types"
import { useCachedProfile, useEventById, useProfile } from "@/hooks/useProfiles"
import { useTranslation } from "@/i18n"
import { NoteContent } from "./NoteContent"
import { NoteActions } from "./NoteActions"
import { Avatar } from "./Avatar"
import { UserName, profilePath } from "./UserName"
import { Timestamp } from "./Timestamp"
import { Icon } from "./Icon"
import { noteIdOf, shortNpub } from "@/utils/nip19"
import { useNoteNavigation } from "./useNoteNavigation"

/** Explicit parent context for a reply. */
export interface ReplyToContext {
  /** The event this note answers, when it could be loaded. */
  event?: NostrEvent
  /** Parent id when the parent event could not be loaded. */
  missingId?: string
}

export interface NoteCardProps {
  event: NostrEvent
  /** Counters to display; when omitted the action bar shows no numbers. */
  engagement?: EngagementResult
  showBookmark?: boolean
  onHashtagClick?: (tag: string) => void
  className?: string
  highlight?: boolean
  /** Disable opening the note (used for embedded / quoted notes). */
  static?: boolean
  /** Hide the action bar (previews inside dialogs). */
  showActions?: boolean
  /** Marks replies written by the thread author with an "OP" badge. */
  opPubkey?: string
  /** Renders "Replying to @name" for replies. */
  replyTo?: ReplyToContext
  /** Called with the signed event after a successful reaction/repost. */
  onReacted?: (event: NostrEvent) => void
  onReposted?: (event: NostrEvent) => void
  /** Called after a reply was published from this note's reply dialog. */
  onReplied?: () => void
}

/** "Replying to @name" line that ties a reply to the note it answers. */
function ReplyContext({ replyTo }: { replyTo: ReplyToContext }) {
  const { t } = useTranslation()
  const parentAuthor = useCachedProfile(replyTo.event?.pubkey)

  if (!replyTo.event) {
    return (
      <p className="note-reply-context missing">
        <Icon name="reply" size={12} />
        {t("Replying to a note that is not available")}
        {replyTo.missingId && <code>{replyTo.missingId.slice(0, 8)}…</code>}
      </p>
    )
  }

  const name =
    parentAuthor?.displayName || parentAuthor?.name || shortNpub(replyTo.event.pubkey)

  return (
    <Link
      to={`/n/${noteIdOf(replyTo.event.id)}`}
      className="note-reply-context"
      data-no-note-nav
      title={t("Open the note this reply answers")}
    >
      <Icon name="reply" size={12} />
      <span>
        {t("Replying to")} <span className="note-reply-context-name">@{name}</span>
      </span>
    </Link>
  )
}

function RepostCard({
  event,
  className,
  static: isStatic,
}: {
  event: NostrEvent
  className: string
  static?: boolean
}) {
  const { targetEventId, embedded } = parseRepostEvent(event)
  const fetched = useEventById(targetEventId)
  const target = embedded ?? fetched
  const author = useProfile(event.pubkey)
  const { openNote } = useNoteNavigation(targetEventId, isStatic)

  return (
    <article
      className={`note-card note-repost ${className} ${isStatic ? "" : "note-card-clickable"}`}
      onClick={isStatic ? undefined : openNote}
      onKeyDown={isStatic ? undefined : openNote}
      tabIndex={isStatic ? undefined : 0}
    >
      <div className="repost-header">
        <Icon name="repeat" size={14} />
        <Link to={profilePath(event.pubkey)} className="repost-author">
          {author?.displayName || author?.name || `${event.pubkey.slice(0, 8)}…`}
        </Link>
        <span>reposted</span>
      </div>
      {target ? (
        <NoteCard event={target} className="note-embedded" static={isStatic} />
      ) : (
        <p className="note-missing">
          The reposted note is not available from the connected relays.{" "}
          {targetEventId && <Link to={`/n/${noteIdOf(targetEventId)}`}>Open it by id</Link>}
        </p>
      )}
    </article>
  )
}

/** One note in a feed, thread or profile. Handles reposts and long form notes. */
export function NoteCard({
  event,
  engagement,
  showBookmark = false,
  onHashtagClick,
  className = "",
  highlight = false,
  static: isStatic = false,
  showActions = true,
  opPubkey,
  replyTo,
  onReacted,
  onReposted,
  onReplied,
}: NoteCardProps) {
  const profile = useProfile(event.pubkey)
  const { openNote } = useNoteNavigation(event.id, isStatic)

  if (event.kind === KINDS.REPOST) {
    return <RepostCard event={event} className={className} static={isStatic} />
  }

  const title = tagValue(event, "title")
  const isArticle = event.kind === KINDS.ARTICLE
  const noteId = noteIdOf(event.id)

  return (
    <article
      className={`note-card ${highlight ? "note-highlight" : ""} ${
        isStatic ? "" : "note-card-clickable"
      } ${className}`}
      onClick={isStatic ? undefined : openNote}
      onKeyDown={isStatic ? undefined : openNote}
      tabIndex={isStatic ? undefined : 0}
    >
      <header className="note-header">
        <Link to={profilePath(event.pubkey)} className="note-avatar-link" aria-label="Open profile">
          <Avatar src={profile?.picture} label={profile?.displayName || event.pubkey} size={40} />
        </Link>
        <div className="note-meta">
          <div className="note-meta-top">
            <UserName pubkey={event.pubkey} profile={profile} />
            {opPubkey && event.pubkey === opPubkey && <span className="badge badge-primary">OP</span>}
            {isArticle && <span className="badge">article</span>}
          </div>
          <div className="note-meta-bottom">
            <span className="note-handle">{profile?.nip05 || `${event.pubkey.slice(0, 8)}…`}</span>
            <span aria-hidden="true">·</span>
            <Link to={`/n/${noteId}`} className="note-time" aria-label="Open note and its replies">
              <Timestamp seconds={event.created_at} />
            </Link>
          </div>
        </div>
      </header>

      {replyTo && <ReplyContext replyTo={replyTo} />}
      {title && <h3 className="note-title">{title}</h3>}
      <NoteContent event={event} onHashtagClick={onHashtagClick} />
      <NoteActions
        event={event}
        engagement={engagement}
        showBookmark={showBookmark}
        onReacted={onReacted}
        onReposted={onReposted}
        onReplied={onReplied}
        hidden={!showActions}
      />
    </article>
  )
}
